import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const TechTools = ({ isDarkMode }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('binary');

  // ========== 1. BINARY TAB ==========
  const [decimal, setDecimal] = useState('25');
  const [binary, setBinary] = useState('11001');
  const [hex, setHex] = useState('19');
  const [textInput, setTextInput] = useState('');
  const [binaryTextInput, setBinaryTextInput] = useState('');

  // ========== 2. NETWORK TAB ==========
  const [ip, setIp] = useState('192.168.1.1');
  const [cidr, setCidr] = useState('24');
  const [netResult, setNetResult] = useState(null);

  // ========== 3. WEBDEV TAB ==========
  const [jsonInput, setJsonInput] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [jsonError, setJsonError] = useState('');

  // ========== 4. SECURITY TAB ==========
  const [passwordLength, setPasswordLength] = useState(12);
  const [includeUpper, setIncludeUpper] = useState(true);
  const [includeLower, setIncludeLower] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [base64Input, setBase64Input] = useState('');
  const [base64Output, setBase64Output] = useState('');
  const [shaInput, setShaInput] = useState('');
  const [shaOutput, setShaOutput] = useState('');

  // ========== 5. DESIGN TAB ==========
  const [colorPicker, setColorPicker] = useState('#3b82f6');
  const [qrText, setQrText] = useState('https://example.com');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [gradientColor1, setGradientColor1] = useState('#ff7e5f');
  const [gradientColor2, setGradientColor2] = useState('#feb47b');
  const [gradientDeg, setGradientDeg] = useState(135);

  // ========== 6. CONVERTERS TAB ==========
  const [lengthValue, setLengthValue] = useState('1');
  const [lengthFrom, setLengthFrom] = useState('m');
  const [lengthTo, setLengthTo] = useState('cm');
  const [lengthResult, setLengthResult] = useState('');
  const [weightValue, setWeightValue] = useState('1');
  const [weightFrom, setWeightFrom] = useState('kg');
  const [weightTo, setWeightTo] = useState('g');
  const [weightResult, setWeightResult] = useState('');
  const [tempValue, setTempValue] = useState('0');
  const [tempFrom, setTempFrom] = useState('c');
  const [tempTo, setTempTo] = useState('f');
  const [tempResult, setTempResult] = useState('');

  // ========== 7. SEO TAB ==========
  const [urlInput, setUrlInput] = useState('');
  const [urlEncoded, setUrlEncoded] = useState('');
  const [wordText, setWordText] = useState('');
  const [wordStats, setWordStats] = useState({ chars: 0, words: 0, sentences: 0 });
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDesc, setMetaDesc] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');

  // ========== 8. UX/UI DESIGN TAB (NEW) ==========
  const [fontSize, setFontSize] = useState('16');
  const [fontFamily, setFontFamily] = useState('Arial, sans-serif');
  const [borderRadius, setBorderRadius] = useState('8');
  const [padding, setPadding] = useState('12');
  const [shadowBlur, setShadowBlur] = useState('10');
  const [previewText, setPreviewText] = useState('სატესტო ტექსტი');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [textColor, setTextColor] = useState('#000000');

  // ========== 9. DATABASES TAB (NEW) ==========
  const [sqlQuery, setSqlQuery] = useState('SELECT * FROM users WHERE id = 1');
  const [sqlResult, setSqlResult] = useState('');
  const [mongoQuery, setMongoQuery] = useState('{ "users": { "$match": { "age": { "$gt": 18 } } } }');
  const [mongoResult, setMongoResult] = useState('');
  const [dbType, setDbType] = useState('sql');

  // ========== 10. API/INTEGRATION TAB (NEW) ==========
  const [apiUrl, setApiUrl] = useState('https://jsonplaceholder.typicode.com/posts/1');
  const [apiMethod, setApiMethod] = useState('GET');
  const [apiHeaders, setApiHeaders] = useState('{\n  "Content-Type": "application/json"\n}');
  const [apiBody, setApiBody] = useState('');
  const [apiResponse, setApiResponse] = useState('');
  const [apiStatus, setApiStatus] = useState('');
  const [apiLoading, setApiLoading] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // ========== Notifications ==========
  const [copyMessage, setCopyMessage] = useState({});
  const showCopyMessage = (id, msg) => {
    setCopyMessage(prev => ({ ...prev, [id]: msg }));
    setTimeout(() => setCopyMessage(prev => ({ ...prev, [id]: null })), 1500);
  };
  const copyToClipboard = async (text, id) => {
    if (!text) { showCopyMessage(id, '❌ ცარიელია'); return; }
    try { await navigator.clipboard.writeText(text); showCopyMessage(id, '✅ კოპირებულია!'); } 
    catch { showCopyMessage(id, '⚠️ ვერ მოხერხდა'); }
  };

  // ========== Binary Functions ==========
  const handleNumberChange = (value, type) => {
    if (value === '') { setDecimal(''); setBinary(''); setHex(''); return; }
    let dec;
    if (type === 'dec') { dec = parseInt(value, 10); if (isNaN(dec)) return; setDecimal(value); setBinary(dec.toString(2)); setHex(dec.toString(16).toUpperCase()); }
    else if (type === 'bin') { if (!/^[0-1]*$/.test(value)) return; setBinary(value); dec = parseInt(value, 2); if (isNaN(dec)) { setDecimal(''); setHex(''); return; } setDecimal(dec.toString(10)); setHex(dec.toString(16).toUpperCase()); }
    else if (type === 'hex') { if (!/^[0-9A-Fa-f]*$/.test(value)) return; setHex(value); dec = parseInt(value, 16); if (isNaN(dec)) { setDecimal(''); setBinary(''); return; } setDecimal(dec.toString(10)); setBinary(dec.toString(2)); }
  };
  const setExample = (type) => {
    if (type === 'dec') { setDecimal('42'); setBinary((42).toString(2)); setHex((42).toString(16).toUpperCase()); }
    else if (type === 'bin') { setBinary('11001'); setDecimal(parseInt('11001', 2).toString(10)); setHex(parseInt('11001', 2).toString(16).toUpperCase()); }
    else if (type === 'hex') { setHex('FF'); setDecimal(parseInt('FF', 16).toString(10)); setBinary(parseInt('FF', 16).toString(2)); }
  };
  const handleTextToBinary = (text) => {
    setTextInput(text);
    setBinaryTextInput(text.split('').map(c => c.charCodeAt(0).toString(2).padStart(8, '0')).join(' '));
  };
  const handleBinaryToText = (bin) => {
    if (!/^[0-1\s]*$/.test(bin)) return;
    setBinaryTextInput(bin);
    const clean = bin.trim().split(/\s+/);
    if (clean.length === 0 || clean[0] === '') { setTextInput(''); return; }
    try { setTextInput(clean.map(b => String.fromCharCode(parseInt(b, 2))).join('')); } catch { setTextInput(''); }
  };

  // ========== Network ==========
  const calculateSubnet = () => {
    const ipParts = ip.split('.').map(Number);
    const cidrNum = parseInt(cidr, 10);
    if (ipParts.length !== 4 || ipParts.some(p => isNaN(p) || p < 0 || p > 255) || isNaN(cidrNum) || cidrNum < 0 || cidrNum > 32) {
      alert("სწორი IP და CIDR (0-32)!"); return;
    }
    const maskBinary = "".padStart(cidrNum, '1').padEnd(32, '0');
    const maskParts = [];
    for (let i = 0; i < 4; i++) maskParts.push(parseInt(maskBinary.slice(i * 8, i * 8 + 8), 2));
    const netParts = ipParts.map((p, i) => p & maskParts[i]);
    const broadcastParts = ipParts.map((p, i) => p | (255 ^ maskParts[i]));
    let hosts = cidrNum === 32 ? 1 : cidrNum === 31 ? 2 : Math.pow(2, 32 - cidrNum) - 2;
    setNetResult({ network: netParts.join('.'), mask: maskParts.join('.'), broadcast: broadcastParts.join('.'), hosts: hosts.toLocaleString() });
  };

  // ========== JSON ==========
  const handleFormatJson = () => {
    try { setJsonError(''); if (!jsonInput.trim()) { setJsonOutput(''); return; } setJsonOutput(JSON.stringify(JSON.parse(jsonInput), null, 4)); } 
    catch (err) { setJsonError('არავალიდური JSON! ' + err.message); setJsonOutput(''); }
  };

  // ========== SECURITY FUNCTIONS ==========
  const generatePassword = () => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    let chars = '';
    if (includeUpper) chars += upper;
    if (includeLower) chars += lower;
    if (includeNumbers) chars += numbers;
    if (includeSymbols) chars += symbols;
    if (chars === '') { setGeneratedPassword('აირჩიეთ ერთი ვარიანტი მაინც'); return; }
    let password = '';
    for (let i = 0; i < passwordLength; i++) password += chars[Math.floor(Math.random() * chars.length)];
    setGeneratedPassword(password);
  };
  const handleBase64 = () => {
    try { setBase64Output(btoa(base64Input)); } catch { setBase64Output('შეცდომა: არავალიდური ტექსტი'); }
  };
  const handleBase64Decode = () => {
    try { setBase64Output(atob(base64Input)); } catch { setBase64Output('შეცდომა: არავალიდური Base64'); }
  };
  const handleSha256 = async () => {
    const encoder = new TextEncoder();
    const data = encoder.encode(shaInput);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    setShaOutput(hashHex);
  };

  // ========== DESIGN FUNCTIONS ==========
  const generateQR = () => {
    if (!qrText.trim()) { setQrCodeUrl(''); return; }
    setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrText)}`);
  };
  const gradientCss = `linear-gradient(${gradientDeg}deg, ${gradientColor1}, ${gradientColor2})`;

  // ========== CONVERTER FUNCTIONS ==========
  const lengthUnits = { m: 1, cm: 0.01, mm: 0.001, km: 1000, inch: 0.0254, ft: 0.3048, mile: 1609.34 };
  const convertLength = () => {
    const meters = parseFloat(lengthValue) * lengthUnits[lengthFrom];
    const result = meters / lengthUnits[lengthTo];
    setLengthResult(result.toFixed(6));
  };
  const weightUnits = { kg: 1, g: 0.001, mg: 0.000001, lb: 0.453592, oz: 0.0283495 };
  const convertWeight = () => {
    const kgs = parseFloat(weightValue) * weightUnits[weightFrom];
    const result = kgs / weightUnits[weightTo];
    setWeightResult(result.toFixed(6));
  };
  const convertTemp = () => {
    let celsius;
    let val = parseFloat(tempValue);
    if (tempFrom === 'c') celsius = val;
    else if (tempFrom === 'f') celsius = (val - 32) * 5/9;
    else celsius = val - 273.15;
    if (tempTo === 'c') setTempResult(celsius.toFixed(2) + ' °C');
    else if (tempTo === 'f') setTempResult((celsius * 9/5 + 32).toFixed(2) + ' °F');
    else setTempResult((celsius + 273.15).toFixed(2) + ' K');
  };

  // ========== SEO FUNCTIONS ==========
  const handleUrlEncode = () => { setUrlEncoded(encodeURIComponent(urlInput)); };
  const handleUrlDecode = () => { try { setUrlEncoded(decodeURIComponent(urlInput)); } catch { setUrlEncoded('შეცდომა'); } };
  const analyzeText = (text) => {
    setWordText(text);
    setWordStats({
      chars: text.length,
      words: text.trim() === '' ? 0 : text.trim().split(/\s+/).length,
      sentences: text.split(/[.!?]+/).filter(s => s.trim().length > 0).length
    });
  };
  const generateMetaTags = () => {
    const metaHtml = `<meta name="description" content="${metaDesc}">\n<meta name="keywords" content="${metaKeywords}">\n<title>${metaTitle}</title>`;
    copyToClipboard(metaHtml, 'metaCopy');
  };

  // ========== UX/UI FUNCTIONS ==========
  const uiPreviewStyle = {
    fontSize: `${fontSize}px`,
    fontFamily: fontFamily,
    borderRadius: `${borderRadius}px`,
    padding: `${padding}px`,
    boxShadow: `0 ${shadowBlur}px ${shadowBlur * 0.5}px rgba(0,0,0,0.1)`,
    backgroundColor: bgColor,
    color: textColor,
    transition: 'all 0.3s ease'
  };

  // ========== DATABASE FUNCTIONS ==========
  const executeSql = () => {
    if (dbType === 'sql') {
      // SQL პარსერის სიმულაცია
      if (sqlQuery.includes('SELECT')) {
        setSqlResult(JSON.stringify([
          { id: 1, name: 'John Doe', email: 'john@example.com', age: 25 },
          { id: 2, name: 'Jane Smith', email: 'jane@example.com', age: 30 },
          { id: 3, name: 'Bob Wilson', email: 'bob@example.com', age: 22 }
        ], null, 2));
      } else if (sqlQuery.includes('INSERT')) {
        setSqlResult('✅ 1 row inserted successfully');
      } else if (sqlQuery.includes('UPDATE')) {
        setSqlResult('✅ 1 row updated successfully');
      } else if (sqlQuery.includes('DELETE')) {
        setSqlResult('✅ 1 row deleted successfully');
      } else {
        setSqlResult('⚠️ Unknown query type. Use SELECT, INSERT, UPDATE, or DELETE');
      }
    } else {
      // MongoDB query simulation
      try {
        const parsed = JSON.parse(mongoQuery);
        setMongoResult(JSON.stringify([
          { _id: '507f1f77bcf86cd799439011', name: 'John', age: 25, city: 'NYC' },
          { _id: '507f1f77bcf86cd799439012', name: 'Jane', age: 30, city: 'LA' }
        ], null, 2));
      } catch {
        setMongoResult('⚠️ Invalid JSON format');
      }
    }
  };

  const handleDbTypeChange = (type) => {
    setDbType(type);
    setSqlResult('');
    setMongoResult('');
  };

  // ========== API FUNCTIONS ==========
  const executeApiCall = async () => {
    setApiLoading(true);
    setApiResponse('');
    setApiStatus('');

    try {
      const options = {
        method: apiMethod,
        headers: apiHeaders ? JSON.parse(apiHeaders) : {},
      };
      if (apiMethod !== 'GET' && apiBody) {
        options.body = apiBody;
      }

      const response = await fetch(apiUrl, options);
      setApiStatus(`${response.status} ${response.statusText}`);
      
      const data = await response.json();
      setApiResponse(JSON.stringify(data, null, 2));
    } catch (error) {
      setApiStatus('❌ Error');
      setApiResponse(`Error: ${error.message}`);
    }
    setApiLoading(false);
  };

  const apiExamples = {
    GET: 'https://jsonplaceholder.typicode.com/posts/1',
    POST: 'https://jsonplaceholder.typicode.com/posts',
    PUT: 'https://jsonplaceholder.typicode.com/posts/1',
    DELETE: 'https://jsonplaceholder.typicode.com/posts/1'
  };

  const setApiExample = (method) => {
    setApiMethod(method);
    setApiUrl(apiExamples[method]);
    if (method === 'POST' || method === 'PUT') {
      setApiBody(JSON.stringify({
        title: 'Test Post',
        body: 'This is a test post',
        userId: 1
      }, null, 2));
    } else {
      setApiBody('');
    }
    setApiResponse('');
    setApiStatus('');
  };

  const inputClass = `w-full p-3 rounded-xl border outline-none font-mono text-base transition-all focus:ring-2 focus:ring-blue-500 ${
    isDarkMode ? 'bg-gray-800 border-gray-700 text-green-400 placeholder-gray-500' : 'bg-white border-gray-300 text-blue-900'
  }`;

  return (
    <div className={`min-h-screen pb-12 transition-colors duration-300 ${isDarkMode ? 'bg-gray-955 text-gray-100' : 'bg-gray-55 text-gray-900'}`}>
      <Helmet><title>DevTools პანელი | 10-ში-1 ხელსაწყოები</title><meta name="description" content="Subnet, რიცხვითი კონვერტერი, JSON, პაროლის გენერატორი, QR, ფერები, კონვერტერები, SEO, UX/UI, მონაცემთა ბაზები, API" /></Helmet>
      
      <nav className={`py-4 px-6 shadow-sm ${isDarkMode ? 'bg-gray-900 border-b border-gray-800' : 'bg-white'}`}>
        <div className="max-w-6xl mx-auto flex justify-between items-center flex-wrap gap-3">
          <button onClick={() => navigate('/')} className="text-blue-500 hover:text-blue-600 font-bold">
            ← {t('nav.backToHome')}
          </button>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="font-black tracking-wide text-lg">⚙️ DevTools პანელი</span>
            <div className="flex gap-2">
              <button 
                onClick={() => changeLanguage('ka')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  i18n.language === 'ka' 
                    ? 'bg-blue-600 text-white' 
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🇬🇪 ქარ
              </button>
              <button 
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  i18n.language === 'en' 
                    ? 'bg-blue-600 text-white' 
                    : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🇬🇧 ENG
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      <main className="max-w-6xl mx-auto mt-10 px-4">
        {/* Tabs - 10 ჩანართი */}
        <div className="flex flex-wrap gap-1 p-1.5 rounded-xl mb-8 bg-gray-200 text-black">
          {['binary', 'network', 'webdev', 'security', 'design', 'converters', 'seo', 'uxui', 'databases', 'api'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === tab ? 'bg-blue-600 text-white shadow' : 'text-gray-600'}`}>
              {tab === 'binary' && '🔢 ბინარული'}
              {tab === 'network' && '🌐 ქსელი'}
              {tab === 'webdev' && '💻 დეველოპმენტი'}
              {tab === 'security' && '🔐 უსაფრთხოება'}
              {tab === 'design' && '🎨 დიზაინი'}
              {tab === 'converters' && '📏 კონვერტერები'}
              {tab === 'seo' && '🔍 SEO/ტექნიკური'}
              {tab === 'uxui' && '✨ UX/UI'}
              {tab === 'databases' && '🗄️ მონაცემები'}
              {tab === 'api' && '🔌 API/ინტეგრაცია'}
            </button>
          ))}
        </div>

        <div className={`p-6 md:p-8 rounded-2xl shadow-xl border ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
          
          {/* ===== BINARY TAB ===== (უცვლელი) ===== */}
          {activeTab === 'binary' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[{ label: '🔟 DECIMAL', type: 'dec', value: decimal, placeholder: '25', example: 'dec' },
                  { label: '⚙️ BINARY', type: 'bin', value: binary, placeholder: '11001', example: 'bin' },
                  { label: '🎨 HEXADECIMAL', type: 'hex', value: hex, placeholder: '19', example: 'hex' }].map(field => (
                  <div key={field.type} className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                    <div className="flex justify-between mb-2"><span className="font-bold text-sm">{field.label}</span><button onClick={() => setExample(field.type)} className="text-xs text-blue-500 underline">📋 მაგალითი</button></div>
                    <input type="text" value={field.value} onChange={(e) => handleNumberChange(e.target.value, field.type)} placeholder={field.placeholder} className={inputClass} />
                    <div className="flex justify-end mt-2"><button onClick={() => copyToClipboard(field.value, `${field.type}Copy`)} className="text-xs bg-gray-500/20 px-3 py-1 rounded-full">📋 კოპირება</button>{copyMessage[`${field.type}Copy`] && <span className="text-xs ml-2 text-green-500">{copyMessage[`${field.type}Copy`]}</span>}</div>
                  </div>
                ))}
              </div>
              <div className="border-t pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="text-xs font-bold text-gray-400">ტექსტი → ბინარული</label><textarea value={textInput} onChange={(e) => handleTextToBinary(e.target.value)} placeholder="ჩაწერე ტექსტი..." className={`${inputClass} h-28`} /></div>
                <div><label className="text-xs font-bold text-gray-400">ბინარული → ტექსტი</label><textarea value={binaryTextInput} onChange={(e) => handleBinaryToText(e.target.value)} placeholder="01000001 01000010" className={`${inputClass} h-28`} /></div>
              </div>
            </div>
          )}

          {/* ===== NETWORK TAB ===== (უცვლელი) ===== */}
          {activeTab === 'network' && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1"><label className="text-xs font-bold text-gray-400">IP მისამართი</label><input type="text" value={ip} onChange={(e) => setIp(e.target.value)} className={inputClass} /></div>
                <div className="w-32"><label className="text-xs font-bold text-gray-400">CIDR</label><input type="number" value={cidr} onChange={(e) => setCidr(e.target.value)} className={inputClass} /></div>
                <button onClick={calculateSubnet} className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl h-[46px]">გამოთვლა ⚡</button>
              </div>
              {netResult && (<div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-800/40 p-4 rounded-xl"><div><p className="text-xs text-gray-400">Network ID</p><p className="font-mono text-blue-400">{netResult.network}</p></div><div><p className="text-xs text-gray-400">Subnet Mask</p><p className="font-mono text-amber-400">{netResult.mask}</p></div><div><p className="text-xs text-gray-400">Broadcast</p><p className="font-mono text-purple-400">{netResult.broadcast}</p></div><div><p className="text-xs text-gray-400">Hosts</p><p className="font-mono text-green-400">{netResult.hosts}</p></div></div>)}
            </div>
          )}

          {/* ===== WEBDEV TAB ===== (უცვლელი) ===== */}
          {activeTab === 'webdev' && (
            <div className="space-y-4">
              <textarea value={jsonInput} onChange={(e) => setJsonInput(e.target.value)} placeholder='{"id":1,"name":"test"}' className={`${inputClass} h-32`} />
              <button onClick={handleFormatJson} className="bg-blue-600 text-white font-bold px-6 py-2.5 rounded-xl">ფორმატირება ✨</button>
              {jsonError && <p className="text-red-400">{jsonError}</p>}
              {jsonOutput && <textarea readOnly value={jsonOutput} className={`${inputClass} h-48 text-emerald-400 bg-gray-950`} />}
            </div>
          )}

          {/* ===== SECURITY TAB ===== (უცვლელი) ===== */}
          {activeTab === 'security' && (
            <div className="space-y-8">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">🔐 პაროლის გენერატორი</h3>
                <div className="flex flex-wrap gap-3 mb-3"><label><input type="checkbox" checked={includeUpper} onChange={() => setIncludeUpper(!includeUpper)} /> A-Z</label><label><input type="checkbox" checked={includeLower} onChange={() => setIncludeLower(!includeLower)} /> a-z</label><label><input type="checkbox" checked={includeNumbers} onChange={() => setIncludeNumbers(!includeNumbers)} /> 0-9</label><label><input type="checkbox" checked={includeSymbols} onChange={() => setIncludeSymbols(!includeSymbols)} /> !@#$</label></div>
                <div className="flex gap-3 mb-3"><span>სიგრძე: {passwordLength}</span><input type="range" min="4" max="32" value={passwordLength} onChange={(e) => setPasswordLength(parseInt(e.target.value))} className="flex-1" /></div>
                <button onClick={generatePassword} className="bg-blue-600 text-white px-4 py-2 rounded-lg">გენერირება 🔄</button>
                {generatedPassword && (<div className="mt-3 flex gap-2"><code className={`flex-1 p-2 rounded ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'} font-mono`}>{generatedPassword}</code><button onClick={() => copyToClipboard(generatedPassword, 'passCopy')} className="px-3 py-2 bg-gray-500 rounded">📋</button>{copyMessage.passCopy && <span className="text-green-500">{copyMessage.passCopy}</span>}</div>)}
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">📦 Base64 კონვერტერი</h3>
                <textarea value={base64Input} onChange={(e) => setBase64Input(e.target.value)} placeholder="ტექსტი ან Base64 კოდი" className={`${inputClass} h-20`} />
                <div className="flex gap-3 mt-2"><button onClick={handleBase64} className="bg-blue-600 text-white px-4 py-2 rounded-lg">ენკოდი →</button><button onClick={handleBase64Decode} className="bg-green-600 text-white px-4 py-2 rounded-lg">← დეკოდი</button></div>
                {base64Output && <div className="mt-3"><code className={`p-2 rounded block ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'} break-all`}>{base64Output}</code></div>}
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">🔒 SHA-256 ჰეშერი</h3>
                <input type="text" value={shaInput} onChange={(e) => setShaInput(e.target.value)} placeholder="ტექსტი" className={inputClass} />
                <button onClick={handleSha256} className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-lg">ჰეშირება</button>
                {shaOutput && <div className="mt-3"><code className={`p-2 rounded block ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'} text-xs break-all`}>{shaOutput}</code></div>}
              </div>
            </div>
          )}

          {/* ===== DESIGN TAB ===== (უცვლელი) ===== */}
          {activeTab === 'design' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <h3 className="font-bold mb-3">🎨 ფერების პიკერი</h3>
                  <input type="color" value={colorPicker} onChange={(e) => setColorPicker(e.target.value)} className="w-full h-20 cursor-pointer" />
                  <p className="mt-2 font-mono text-center">{colorPicker}</p>
                  <button onClick={() => copyToClipboard(colorPicker, 'colorCopy')} className="w-full mt-2 bg-blue-600 text-white py-1 rounded">კოპირება</button>
                </div>
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <h3 className="font-bold mb-3">📱 QR კოდი</h3>
                  <input type="text" value={qrText} onChange={(e) => setQrText(e.target.value)} placeholder="https://..." className={inputClass} />
                  <button onClick={generateQR} className="w-full mt-2 bg-blue-600 text-white py-2 rounded-lg">გენერირება</button>
                  {qrCodeUrl && <img src={qrCodeUrl} alt="QR" className="mt-3 mx-auto border p-2 rounded-xl bg-white" />}
                </div>
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <h3 className="font-bold mb-3">🌈 გრადიენტის გენერატორი</h3>
                  <div className="flex gap-2"><input type="color" value={gradientColor1} onChange={(e) => setGradientColor1(e.target.value)} /><input type="color" value={gradientColor2} onChange={(e) => setGradientColor2(e.target.value)} /></div>
                  <input type="range" min="0" max="360" value={gradientDeg} onChange={(e) => setGradientDeg(e.target.value)} className="w-full mt-2" />
                  <div className="h-20 rounded-lg mt-2" style={{ background: gradientCss }}></div>
                  <code className={`text-xs block mt-2 p-1 rounded ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'}`}>{gradientCss}</code>
                  <button onClick={() => copyToClipboard(gradientCss, 'gradCopy')} className="w-full mt-2 bg-blue-600 text-white py-1 rounded">კოპირება CSS</button>
                </div>
              </div>
            </div>
          )}

          {/* ===== CONVERTERS TAB ===== (უცვლელი) ===== */}
          {activeTab === 'converters' && (
            <div className="space-y-8">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">📏 სიგრძის კონვერტერი</h3>
                <div className="flex gap-2 flex-wrap"><input type="number" value={lengthValue} onChange={(e) => setLengthValue(e.target.value)} className="w-28 p-2 rounded border" /><select value={lengthFrom} onChange={(e) => setLengthFrom(e.target.value)} className="p-2 rounded border"><option value="m">მეტრი</option><option value="cm">სმ</option><option value="mm">მმ</option><option value="km">კმ</option><option value="inch">დუიმი</option><option value="ft">ფუტი</option></select><span>→</span><select value={lengthTo} onChange={(e) => setLengthTo(e.target.value)} className="p-2 rounded border"><option value="m">მეტრი</option><option value="cm">სმ</option><option value="mm">მმ</option><option value="km">კმ</option><option value="inch">დუიმი</option><option value="ft">ფუტი</option></select><button onClick={convertLength} className="bg-blue-600 text-white px-4 py-2 rounded">გადაყვანა</button></div>
                {lengthResult && <p className="mt-2 font-bold">შედეგი: {lengthResult}</p>}
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">⚖️ წონის კონვერტერი</h3>
                <div className="flex gap-2 flex-wrap"><input type="number" value={weightValue} onChange={(e) => setWeightValue(e.target.value)} className="w-28 p-2 rounded border" /><select value={weightFrom} onChange={(e) => setWeightFrom(e.target.value)} className="p-2 rounded border"><option value="kg">კგ</option><option value="g">გ</option><option value="mg">მგ</option><option value="lb">ფუნტი</option><option value="oz">უნცია</option></select><span>→</span><select value={weightTo} onChange={(e) => setWeightTo(e.target.value)} className="p-2 rounded border"><option value="kg">კგ</option><option value="g">გ</option><option value="mg">მგ</option><option value="lb">ფუნტი</option><option value="oz">უნცია</option></select><button onClick={convertWeight} className="bg-blue-600 text-white px-4 py-2 rounded">გადაყვანა</button></div>
                {weightResult && <p className="mt-2 font-bold">შედეგი: {weightResult}</p>}
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">🌡️ ტემპერატურის კონვერტერი</h3>
                <div className="flex gap-2 flex-wrap"><input type="number" value={tempValue} onChange={(e) => setTempValue(e.target.value)} className="w-28 p-2 rounded border" /><select value={tempFrom} onChange={(e) => setTempFrom(e.target.value)} className="p-2 rounded border"><option value="c">°C</option><option value="f">°F</option><option value="k">K</option></select><span>→</span><select value={tempTo} onChange={(e) => setTempTo(e.target.value)} className="p-2 rounded border"><option value="c">°C</option><option value="f">°F</option><option value="k">K</option></select><button onClick={convertTemp} className="bg-blue-600 text-white px-4 py-2 rounded">გადაყვანა</button></div>
                {tempResult && <p className="mt-2 font-bold">შედეგი: {tempResult}</p>}
              </div>
            </div>
          )}

          {/* ===== SEO TAB ===== (უცვლელი) ===== */}
          {activeTab === 'seo' && (
            <div className="space-y-6">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">🔗 URL ენკოდერი/დეკოდერი</h3>
                <textarea value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://example.com?name=გამარჯობა" className={`${inputClass} h-20`} />
                <div className="flex gap-3 mt-2"><button onClick={handleUrlEncode} className="bg-blue-600 text-white px-4 py-2 rounded">ენკოდი →</button><button onClick={handleUrlDecode} className="bg-green-600 text-white px-4 py-2 rounded">← დეკოდი</button></div>
                {urlEncoded && <div className="mt-3"><code className={`p-2 rounded block ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'} break-all`}>{urlEncoded}</code></div>}
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">📝 სიტყვების მთვლელი</h3>
                <textarea value={wordText} onChange={(e) => analyzeText(e.target.value)} placeholder="ჩაწერე ტექსტი..." className={`${inputClass} h-28`} />
                <div className="flex gap-4 mt-3"><span>📖 სიმბოლოები: {wordStats.chars}</span><span>📝 სიტყვები: {wordStats.words}</span><span>📄 წინადადებები: {wordStats.sentences}</span></div>
              </div>
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">🏷️ Meta Tag გენერატორი</h3>
                <input type="text" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} placeholder="სათაური (Title)" className={`${inputClass} mb-2`} />
                <textarea value={metaDesc} onChange={(e) => setMetaDesc(e.target.value)} placeholder="აღწერა (Description)" className={`${inputClass} h-20 mb-2`} />
                <input type="text" value={metaKeywords} onChange={(e) => setMetaKeywords(e.target.value)} placeholder="საკვანძო სიტყვები (Keywords)" className={`${inputClass} mb-2`} />
                <button onClick={generateMetaTags} className="bg-blue-600 text-white px-4 py-2 rounded-lg">კოპირება Meta Tags 📋</button>
                {copyMessage.metaCopy && <span className="ml-2 text-green-500">{copyMessage.metaCopy}</span>}
              </div>
            </div>
          )}

          {/* ===== UX/UI DESIGN TAB (NEW) ===== */}
          {activeTab === 'uxui' && (
            <div className="space-y-6">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">🎨 UI პარამეტრები</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400">🔤 შრიფტის ზომა: {fontSize}px</label>
                    <input type="range" min="10" max="40" value={fontSize} onChange={(e) => setFontSize(e.target.value)} className="w-full" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">🎨 შრიფტის ტიპი</label>
                    <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} className={`w-full p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="'Courier New', monospace">Courier New</option>
                      <option value="'Segoe UI', sans-serif">Segoe UI</option>
                      <option value="'Roboto', sans-serif">Roboto</option>
                      <option value="'Inter', sans-serif">Inter</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">📐 კუთხე: {borderRadius}px</label>
                    <input type="range" min="0" max="50" value={borderRadius} onChange={(e) => setBorderRadius(e.target.value)} className="w-full" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">📏 პადინგი: {padding}px</label>
                    <input type="range" min="4" max="40" value={padding} onChange={(e) => setPadding(e.target.value)} className="w-full" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">🌑 ჩრდილი: {shadowBlur}px</label>
                    <input type="range" min="0" max="30" value={shadowBlur} onChange={(e) => setShadowBlur(e.target.value)} className="w-full" />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-gray-400">🎨 ფონი</label>
                      <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="w-full h-10" />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-400">✏️ ტექსტი</label>
                      <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="w-full h-10" />
                    </div>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">👀 პრევიუ</h3>
                <div>
                  <input type="text" value={previewText} onChange={(e) => setPreviewText(e.target.value)} placeholder="სატესტო ტექსტი" className={`${inputClass} mb-3`} />
                  <div style={uiPreviewStyle} className="min-h-[100px] flex items-center justify-center">
                    <p className="text-center">{previewText}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => copyToClipboard(JSON.stringify(uiPreviewStyle, null, 2), 'uiCopy')} className="bg-blue-600 text-white px-4 py-2 rounded">📋 CSS კოპირება</button>
                  {copyMessage.uiCopy && <span className="text-green-500">{copyMessage.uiCopy}</span>}
                </div>
              </div>
            </div>
          )}

          {/* ===== DATABASES TAB (NEW) ===== */}
          {activeTab === 'databases' && (
            <div className="space-y-6">
              <div className="flex gap-2 mb-4">
                <button onClick={() => handleDbTypeChange('sql')} className={`px-4 py-2 rounded-lg font-bold ${dbType === 'sql' ? 'bg-blue-600 text-white' : 'bg-gray-600/30'}`}>
                  🗄️ SQL
                </button>
                <button onClick={() => handleDbTypeChange('mongo')} className={`px-4 py-2 rounded-lg font-bold ${dbType === 'mongo' ? 'bg-blue-600 text-white' : 'bg-gray-600/30'}`}>
                  🍃 MongoDB
                </button>
              </div>

              {dbType === 'sql' ? (
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <h3 className="font-bold mb-3">📊 SQL Query</h3>
                  <textarea value={sqlQuery} onChange={(e) => setSqlQuery(e.target.value)} className={`${inputClass} h-28`} />
                  <div className="flex gap-3 mt-2 flex-wrap">
                    <button onClick={executeSql} className="bg-blue-600 text-white px-4 py-2 rounded">▶️ Execute</button>
                    <button onClick={() => setSqlQuery('SELECT * FROM users WHERE age > 18')} className="bg-green-600 text-white px-4 py-2 rounded">📋 SELECT</button>
                    <button onClick={() => setSqlQuery('INSERT INTO users (name, email, age) VALUES ("John", "john@test.com", 25)')} className="bg-yellow-600 text-white px-4 py-2 rounded">📝 INSERT</button>
                    <button onClick={() => setSqlQuery('UPDATE users SET age = 26 WHERE id = 1')} className="bg-orange-600 text-white px-4 py-2 rounded">🔄 UPDATE</button>
                    <button onClick={() => setSqlQuery('DELETE FROM users WHERE id = 1')} className="bg-red-600 text-white px-4 py-2 rounded">🗑️ DELETE</button>
                  </div>
                  {sqlResult && <div className="mt-3"><code className={`p-3 rounded block ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'} text-xs whitespace-pre`}>{sqlResult}</code></div>}
                </div>
              ) : (
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <h3 className="font-bold mb-3">🍃 MongoDB Query</h3>
                  <textarea value={mongoQuery} onChange={(e) => setMongoQuery(e.target.value)} className={`${inputClass} h-28`} />
                  <div className="flex gap-3 mt-2">
                    <button onClick={executeSql} className="bg-blue-600 text-white px-4 py-2 rounded">▶️ Execute</button>
                    <button onClick={() => setMongoQuery('{ "users": { "$match": { "age": { "$gt": 18 } } } }')} className="bg-green-600 text-white px-4 py-2 rounded">📋 Aggregate</button>
                    <button onClick={() => setMongoQuery('{ "find": "users", "filter": { "city": "NYC" } }')} className="bg-purple-600 text-white px-4 py-2 rounded">🔍 Find</button>
                  </div>
                  {mongoResult && <div className="mt-3"><code className={`p-3 rounded block ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'} text-xs whitespace-pre`}>{mongoResult}</code></div>}
                </div>
              )}
            </div>
          )}

          {/* ===== API/INTEGRATION TAB (NEW) ===== */}
          {activeTab === 'api' && (
            <div className="space-y-6">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <h3 className="font-bold mb-3">🔌 API Tester</h3>
                <div className="flex gap-3 mb-3 flex-wrap">
                  <button onClick={() => setApiExample('GET')} className={`px-3 py-1 rounded ${apiMethod === 'GET' ? 'bg-blue-600 text-white' : 'bg-gray-600/30'}`}>GET</button>
                  <button onClick={() => setApiExample('POST')} className={`px-3 py-1 rounded ${apiMethod === 'POST' ? 'bg-green-600 text-white' : 'bg-gray-600/30'}`}>POST</button>
                  <button onClick={() => setApiExample('PUT')} className={`px-3 py-1 rounded ${apiMethod === 'PUT' ? 'bg-yellow-600 text-white' : 'bg-gray-600/30'}`}>PUT</button>
                  <button onClick={() => setApiExample('DELETE')} className={`px-3 py-1 rounded ${apiMethod === 'DELETE' ? 'bg-red-600 text-white' : 'bg-gray-600/30'}`}>DELETE</button>
                </div>
                <input type="text" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} placeholder="https://api.example.com/endpoint" className={inputClass} />
                <div className="mt-3">
                  <label className="text-xs text-gray-400">Headers (JSON)</label>
                  <textarea value={apiHeaders} onChange={(e) => setApiHeaders(e.target.value)} className={`${inputClass} h-20`} />
                </div>
                {(apiMethod === 'POST' || apiMethod === 'PUT') && (
                  <div className="mt-3">
                    <label className="text-xs text-gray-400">Body (JSON)</label>
                    <textarea value={apiBody} onChange={(e) => setApiBody(e.target.value)} className={`${inputClass} h-28`} />
                  </div>
                )}
                <button onClick={executeApiCall} disabled={apiLoading} className="mt-3 bg-blue-600 text-white px-6 py-2 rounded-lg font-bold">
                  {apiLoading ? '⏳ იტვირთება...' : '🚀 Send Request'}
                </button>
                {apiStatus && <p className="mt-2 text-sm font-bold">{apiStatus}</p>}
                {apiResponse && (
                  <div className="mt-3">
                    <div className="flex justify-between">
                      <label className="text-xs text-gray-400">Response</label>
                      <button onClick={() => copyToClipboard(apiResponse, 'apiCopy')} className="text-xs text-blue-500">📋 კოპირება</button>
                    </div>
                    <code className={`p-3 rounded block ${isDarkMode ? 'bg-gray-900' : 'bg-gray-200'} text-xs whitespace-pre max-h-96 overflow-auto`}>{apiResponse}</code>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default TechTools;